import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Send, MessageSquare } from "lucide-react";
import { buttonText } from "@/lib/buttonTexts";
import { ekkoText } from "@/lib/ekkoDefaults";
import { safeUrl } from "@/lib/whatsapp";

const INPUT = "w-full rounded-xl border border-[#2A2A2A] bg-[#111111] px-4 py-3 text-[#F5F5F5] focus:border-[#A6C48A] focus:ring-2 focus:ring-[#A6C48A]/40 transition-colors";

function Field({ label, optional = false, htmlFor, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-bold text-[#F5F5F5] mb-2">
        {label}{optional && <span className="font-normal text-[#9A9A9A]"> (opcional)</span>}
      </label>
      {children}
    </div>
  );
}

export default function ContactSection({ onSubmit, content = {} }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telemovel: "",
    mensagem: "",
    tipo: "aula_experimental",
  });
  
  const contactTitle = ekkoText(content, "contact_title");
  const contactSubtitle = ekkoText(content, "contact_subtitle");
  const address = ekkoText(content, "contact_address");
  const phone = ekkoText(content, "contact_phone");
  const email = ekkoText(content, "contact_email");
  const hours = ekkoText(content, "opening_hours");
  const handle = ekkoText(content, "instagram_handle");
  const instagram = safeUrl(ekkoText(content, "instagram_url"));
  const mapUrl = safeUrl(content.contact_map_url);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const success = await onSubmit(formData);
    if (success) {
      setFormData({
        nome: "",
        email: "",
        telemovel: "",
        mensagem: "",
        tipo: "aula_experimental",
      });
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 5000);
    }
    setLoading(false);
  };

  return (
    <section
      id="contacto"
      ref={ref}
      className="py-24 md:py-32 bg-[#010000] relative"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left: Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F5F5F5] mb-4 tracking-wider rich-text-content"
              data-testid="contact-title"
              dangerouslySetInnerHTML={{ __html: contactTitle }}
            />
            <p 
              className="text-[#C9C9C9] text-base md:text-lg mb-10 rich-text-content"
              data-testid="contact-subtitle"
              dangerouslySetInnerHTML={{ __html: contactSubtitle }}
            />

            <form
              onSubmit={handleSubmit}
              className="space-y-6"
              data-testid="contact-form"
            >
              <Field label="Nome" htmlFor="contacto-nome">
                <input id="contacto-nome" type="text" name="nome" autoComplete="name" value={formData.nome} onChange={handleChange} required className={INPUT} data-testid="contact-input-nome" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Telemóvel" htmlFor="contacto-telemovel">
                  <input id="contacto-telemovel" type="tel" name="telemovel" autoComplete="tel" value={formData.telemovel} onChange={handleChange} required className={INPUT} data-testid="contact-input-telemovel" />
                </Field>
                <Field label="Email" optional htmlFor="contacto-email">
                  <input id="contacto-email" type="email" name="email" autoComplete="email" value={formData.email} onChange={handleChange} className={INPUT} data-testid="contact-input-email" />
                </Field>
              </div>
              <Field label="Mensagem" optional htmlFor="contacto-mensagem">
                <textarea id="contacto-mensagem" name="mensagem" rows={3} value={formData.mensagem} onChange={handleChange} className={`${INPUT} resize-none`} data-testid="contact-input-mensagem" />
              </Field>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="btn-skew w-full bg-[#A6C48A] text-[#010000] py-4 font-bold uppercase tracking-wider hover:bg-[#B9D3A1] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                data-testid="contact-form-submit"
              >
                <span>{loading ? "A enviar..." : buttonText(content, "contact_submit")}</span>
                <Send size={18} className="transform skew-x-[10deg]" />
              </button>

              <p className="flex items-center justify-center gap-2 text-sm text-[#C9C9C9]">
                <MessageSquare size={16} className="text-[#A6C48A]" />
                Ao enviar, abrimos o WhatsApp com a tua mensagem pronta.
              </p>

              {/* Success Message */}
              {submitted && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-[#A6C48A]/20 border border-[#A6C48A] rounded-xl p-4 text-center"
                  data-testid="contact-success-message"
                >
                  <p className="text-[#A6C48A] font-semibold">
                    Abrimos o WhatsApp com a tua mensagem.
                  </p>
                  <p className="text-[#C9C9C9] text-sm mt-1">
                    Só falta carregares em enviar na conversa.
                  </p>
                </motion.div>
              )}
            </form>
          </motion.div>

          {/* Right: Map & Info */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-8"
          >
            {/* Informações do EKKO (painel → Contacto) */}
            <div className="bg-[#111111] border border-[#2A2A2A] p-6 rounded-2xl">
              <h3 className="text-xl font-bold text-[#A6C48A] tracking-wider mb-4">LOCALIZAÇÃO</h3>
              <p className="text-[#C9C9C9]">{address}</p>
              {mapUrl && (
                <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="inline-block text-[#F5F5F5] font-bold text-sm mt-3 underline underline-offset-4">
                  Ver no mapa
                </a>
              )}
            </div>

            <div className="bg-[#111111] border border-[#2A2A2A] p-6 rounded-2xl">
              <h3 className="text-xl font-bold text-[#A6C48A] tracking-wider mb-4">CONTACTOS</h3>
              <div className="text-[#C9C9C9] space-y-1">
                {phone && <p>Telefone: <a className="underline underline-offset-4" href={`tel:${phone.replace(/[^\d+]/g, "")}`}>{phone}</a></p>}
                {email && <p>Email: <a className="underline underline-offset-4" href={`mailto:${email}`}>{email}</a></p>}
                {instagram && <p>Instagram: <a className="underline underline-offset-4" href={instagram} target="_blank" rel="noopener noreferrer">{handle}</a></p>}
              </div>
            </div>

            {hours && (
              <div className="bg-[#111111] border border-[#2A2A2A] p-6 rounded-2xl">
                <h3 className="text-xl font-bold text-[#A6C48A] tracking-wider mb-4">HORÁRIO</h3>
                <p className="text-[#C9C9C9] text-sm">{hours}</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
